import type { ComputedRef, InjectionKey } from "vue";
import type { ChoiceTileColumns, ChoiceTileLayout, ChoiceTileSize, ChoiceTileVariant } from "../ChoiceTile/choiceTile.types";

export type CheckboxTileValue = string | number;

export interface VCheckboxTileGroupProps {
  /** Visible label above the tiles; it names the group. Without it, pass `ariaLabelledby` or `ariaLabel`. */
  label?: string
  /** Help text behind a question mark after the label. */
  help?: string
  /** Accessible name of the group when no visible label names it. */
  ariaLabel?: string
  /** Id of a visible heading outside the group that names it. Ignored when `label` is set. */
  ariaLabelledby?: string
  /** Name shared by the checkboxes; a unique one is generated when omitted. */
  name?: string
  /** See {@link ChoiceTileLayout}; a row tile needs about 240px, a stacked tile about 180px. */
  layout?: ChoiceTileLayout
  /** See {@link ChoiceTileColumns}. */
  columns?: ChoiceTileColumns
  /** How every tile of the group is laid out; see {@link ChoiceTileVariant}. Defaults to `row`. */
  variant?: ChoiceTileVariant
  /** Disables every tile of the group. */
  disabled?: boolean
  /** At least one tile must be checked: marks the label and sets `min` to at least 1. */
  required?: boolean
  /**
   * The fewest tiles that must be checked. Once the user changed the selection (or `validate()` ran) and fewer are
   * checked, the group shows "Kies er minstens één" (or the count) under the tiles.
   */
  min?: number
  /** An error from the form, such as a vee-validate message. Shown instead of the group's own `min` error. */
  errorMessage?: string
  /**
   * Shows the group's own error when fewer than `min` tiles are checked. Turn it off when a form validates the
   * selection and passes its message through `errorMessage`, so the two never show one after the other.
   */
  showMinError?: boolean
  testId?: string
}

export interface VCheckboxTileProps {
  value: CheckboxTileValue
  title: string
  subtitle?: string
  /** Disables this tile only; the group can disable all of them. */
  disabled?: boolean
  /**
   * Why this tile cannot be changed, such as a scope that is always on. Locks the tile in its current state and shows
   * the reason in a tooltip; the checkbox stays focusable so keyboard users can read the reason too.
   */
  disabledReason?: string
  /** See {@link ChoiceTileSize}. */
  size?: ChoiceTileSize
  testId?: string
}

export interface CheckboxTileRegistration {
  value: () => CheckboxTileValue
  /** The checkbox of the tile, for its position in the document. */
  element: () => HTMLElement | null
}

export interface CheckboxTileGroupContext {
  name: ComputedRef<string>
  modelValue: ComputedRef<CheckboxTileValue[]>
  disabled: ComputedRef<boolean>
  invalid: ComputedRef<boolean>
  errorId: ComputedRef<string | undefined>
  variant: ComputedRef<ChoiceTileVariant>
  /** Registers a tile so the model keeps the checked values in the order of the tiles; returns the unregister. */
  register: (tile: CheckboxTileRegistration) => () => void
  toggle: (value: CheckboxTileValue, checked: boolean) => void
}

export const CHECKBOX_TILE_GROUP_KEY: InjectionKey<CheckboxTileGroupContext> = Symbol("checkboxTileGroup");
